import { useEffect, useState, type PropsWithChildren } from 'react'
import { apiGet } from '../api'
import type { CutoffSettings } from '../api'
import CutoffOverlay from './CutoffOverlay'

const enGB = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London' })

function londonParts(d: Date): { weekday: number; minutes: number } {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  })
  const parts = fmt.formatToParts(d)
  const wmap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
  const weekday = wmap[parts.find(p => p.type === 'weekday')?.value ?? 'Mon'] ?? 1
  const h = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0', 10)
  const m = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10)
  return { weekday, minutes: h * 60 + m }
}

function parseHHMM(s: string): number {
  const [h, m] = s.split(':').map(n => parseInt(n, 10))
  return (h ?? 0) * 60 + (m ?? 0)
}

const SNOOZE_KEY = 'cutoff-snoozed-until'

export default function Shell({ children }: PropsWithChildren) {
  const [cutoff, setCutoff] = useState<CutoffSettings | null>(null)
  const [now, setNow] = useState(() => new Date())
  const [snoozedUntil, setSnoozedUntil] = useState<number>(() => {
    const v = localStorage.getItem(SNOOZE_KEY)
    return v ? parseInt(v, 10) || 0 : 0
  })

  useEffect(() => {
    apiGet<CutoffSettings>('/settings/cutoff').then(setCutoff).catch(() => setCutoff({ weekday: '21:30', weekend: '22:00' }))
  }, [])

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(t)
  }, [])

  let active = false
  let minutesToCutoff = 0
  let cutoffMinutes = 0
  if (cutoff) {
    const { weekday, minutes } = londonParts(now)
    const isWeekend = weekday === 0 || weekday === 6
    cutoffMinutes = parseHHMM(isWeekend ? cutoff.weekend : cutoff.weekday)
    active = minutes >= cutoffMinutes && now.getTime() > snoozedUntil
    minutesToCutoff = cutoffMinutes - minutes
    document.body.dataset.cutoffActive = String(active)
  }

  const snooze = () => {
    const until = Date.now() + 30 * 60_000
    localStorage.setItem(SNOOZE_KEY, String(until))
    setSnoozedUntil(until)
    fetch((import.meta.env.VITE_API_BASE as string ?? '') + '/admin/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('jamie-dashboard-bearer') ?? ''}` },
      body: JSON.stringify({ kind: 'cutoff_snooze', detail: '30m' }),
    }).catch(() => { /* fire and forget */ })
  }

  const cutoffLabel = active
    ? null
    : cutoff
      ? minutesToCutoff > 60
        ? `${Math.floor(minutesToCutoff / 60)}h ${minutesToCutoff % 60}m to cutoff`
        : `${minutesToCutoff} minutes to cutoff`
      : ''

  return (
    <div className="app">
      <header className="masthead">
        <div className="date">{enGB.format(now)} · <span className="mono">{time.format(now)}</span></div>
        {cutoffLabel !== null && <div className="cutoff">{cutoffLabel}</div>}
      </header>
      {active && <CutoffOverlay onSnooze={snooze} />}
      {children}
    </div>
  )
}
